import { useMemo, useState } from 'react';
import type { NewsBriefingDetail, NewsStory } from '../../types';
import ShareArtifactControl from '../common/ShareArtifactControl';
import HandoffControl from '../common/HandoffControl';
import { useShellNavigate } from '../common/ShellLink';

/**
 * One briefing, as its READER sees it: the stories, grouped and sorted by the
 * beat's own attributes.
 *
 * "Already reported" stays here because it is about the content — the first
 * question anyone asks of a summary is "did it miss something?", and a feed
 * that silently swallows six follow-ups is one you stop believing. The rest of
 * what did not make it — dropped by filters, never judged, the run log, the
 * tags — moved into RunInspector, because those are questions about the
 * SETTINGS and they belong next to the settings. What is left below is a
 * one-line pointer so the reader still knows the fuller account exists.
 *
 * Grouping is client-side over a few dozen rows — a round trip per toggle would
 * be slower than the regrouping it replaces.
 */

const UNTAGGED = '(untagged)';

function StoryCard({ story }: { story: NewsStory }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3 flex items-start gap-3"
      >
        <span
          className={`mt-1 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        >
          ›
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            {story.is_update && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                Update
              </span>
            )}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {story.headline}
            </span>
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            {Object.entries(story.attributes ?? {}).map(([k, v]) => (
              <span
                key={k}
                className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700"
              >
                {k}: {v}
              </span>
            ))}
            <span>
              {story.article_count} source{story.article_count === 1 ? '' : 's'}
            </span>
          </span>
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 pl-11 space-y-3">
          {story.is_update && story.new_since_last ? (
            <>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  What's new
                </h4>
                <p className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">
                  {story.new_since_last}
                </p>
              </div>
              {story.thread_digest && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400">
                    Previously reported
                  </summary>
                  <pre className="mt-1 whitespace-pre-wrap font-sans text-xs text-gray-600 dark:text-gray-400">
                    {story.thread_digest}
                  </pre>
                </details>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">
              {story.summary}
            </p>
          )}

          {story.why_it_matters && (
            <p className="text-sm text-gray-600 dark:text-gray-400 italic">
              {story.why_it_matters}
            </p>
          )}

          {story.articles.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Sources
              </h4>
              <ul className="space-y-1">
                {story.articles.map((a) => (
                  <li key={a.id} className="text-sm">
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      {a.title}
                    </a>
                    {a.source_name && (
                      <span className="text-gray-500 dark:text-gray-400">
                        {' '}
 - {a.source_name}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BriefingView({
  briefing,
  onInspect,
}: {
  briefing: NewsBriefingDetail;
  /** Opens the run inspector on this briefing. Absent when there is nothing to
   *  open it from. */
  onInspect?: () => void;
}) {
  const [groupBy, setGroupBy] = useState('');
  const [sortBy, setSortBy] = useState<'order' | 'sources'>('order');

  const attributeNames = briefing.attributes.map((a) => a.name);

  const sorted = useMemo(() => {
    const rows = [...briefing.stories];
    if (sortBy === 'sources') {
      rows.sort((a, b) => b.article_count - a.article_count);
    } else {
      rows.sort((a, b) => a.sort_order - b.sort_order);
    }
    return rows;
  }, [briefing.stories, sortBy]);

  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const buckets = new Map<string, NewsStory[]>();
    for (const story of sorted) {
      const key = story.attributes?.[groupBy] || UNTAGGED;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(story);
    }
    // Named buckets alphabetically, with the untagged pile last — it is the
    // least interesting group and shouldn't sit above real ones.
    return Array.from(buckets.entries()).sort(([a], [b]) => {
      if (a === UNTAGGED) return 1;
      if (b === UNTAGGED) return -1;
      return a.localeCompare(b);
    });
  }, [sorted, groupBy]);

  // The handoffs below leave this product for another one, which lives at
  // another host — see shell/routeShell.ts.
  const shellNavigate = useShellNavigate();
  const empty = briefing.stories.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-gray-500 dark:text-gray-400">
          {briefing.articles_fetched} fetched · {briefing.stories.length} stories
          {briefing.updates_written > 0 && ` · ${briefing.updates_written} updates`}
        </span>
        <div className="flex-1" />
        {/* The briefing's only way out of the admin UI. */}
        <ShareArtifactControl kind="NEWS_BRIEFING" newsBriefingId={briefing.id} />
        {/* WHERE THIS BRIEFING CAN GO NEXT. Beside Share on purpose: this is the
            same question — "who else should see this" — answered inside the
            product rather than outside it. Locked when the other product is not
            held, because that is where someone learns the two are worth having
            together. */}
        {/* "Send to a brainstorm" stood here. It went with the product, and
            nothing ever read the `from_briefing` it handed over. */}
        <HandoffControl
          product="PUBLISH"
          label="Start a subject"
          unlockedBy="Turn this briefing into a subject you can publish about"
          onClick={() => shellNavigate(`/subjects?from_briefing=${briefing.id}`)}
        />
        {attributeNames.length > 0 && (
          <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
            Group by
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-xs"
            >
              <option value="">Nothing</option>
              {attributeNames.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          Sort by
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'order' | 'sources')}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-xs"
          >
            <option value="order">Briefing order</option>
            <option value="sources">Most sources</option>
          </select>
        </label>
      </div>

      {briefing.last_error && (
        // `break-words` + `whitespace-pre-wrap`: these messages carry URLs and
        // query strings with no spaces to break on, so without it the text runs
        // off the right edge of the card and the end — which is the part that
        // says what went wrong — is unreadable.
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap break-words max-h-60 overflow-y-auto">
          {briefing.last_error}
        </div>
      )}

      {empty ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-600 dark:text-gray-300">
          {briefing.ended_reason === 'NO_ARTICLES'
            ? 'Nothing was published in this window.'
            : briefing.status === 'RUNNING' || briefing.status === 'QUEUED'
              ? 'Building…'
              : 'No stories in this briefing.'}
        </div>
      ) : grouped ? (
        <div className="space-y-5">
          {grouped.map(([bucket, stories]) => (
            <div key={bucket}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {groupBy}: {bucket}{' '}
                <span className="font-normal">({stories.length})</span>
              </h3>
              <div className="space-y-2">
                {stories.map((s: NewsStory) => (
                  <StoryCard key={s.id} story={s} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((s) => (
            <StoryCard key={s.id} story={s} />
          ))}
        </div>
      )}

      {briefing.muted.length > 0 && (
        <details className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
          <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-300">
            Already reported ({briefing.muted.length})
          </summary>
          <p className="mt-1 mb-2 text-xs text-gray-500 dark:text-gray-400">
            More coverage arrived for stories this beat has already told you
            about, and none of it added anything new.
          </p>
          <ul className="space-y-2">
            {briefing.muted.map((story) => (
              <li key={story.id} className="text-sm">
                <span className="text-gray-800 dark:text-gray-200">
                  {story.thread_title ?? story.headline}
                </span>
                {story.suppressed_reason && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {' '}
 - {story.suppressed_reason}
                  </span>
                )}
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {' '}
                  ({story.article_count} article
                  {story.article_count === 1 ? '' : 's'})
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {(briefing.rejected.length > 0 || briefing.unreviewed.length > 0) && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {briefing.rejected.length > 0 &&
            `${briefing.rejected.length} article(s) dropped by filters`}
          {briefing.rejected.length > 0 && briefing.unreviewed.length > 0 && ' · '}
          {briefing.unreviewed.length > 0 &&
            `${briefing.unreviewed.length} never judged`}
          {onInspect && (
            <>
              {' - '}
              <button onClick={onInspect} className="underline hover:no-underline">
                see how this run went
              </button>
            </>
          )}
        </p>
      )}

    </div>
  );
}
